// MST
import { types, flow, cast } from 'mobx-state-tree'

import {
  IAboutUsProfileGetAllAttrs,
  IAboutUsProfileGetAttrs,
  IAboutUsProfileSaveAttrs,
} from 'interfaces/back-office/content-management/about-us/profile/about-us-profile-view.interface'
import {
  IAboutUsProfileApiGetAllResponse,
  IAboutUsProfileApiGetResponse,
  IAboutUsProfileApiSaveResponse,
} from 'interfaces/back-office/content-management/about-us/profile/about-us-profile-api.interface'
import { IAboutUsProfile } from 'interfaces/back-office/content-management/about-us/profile'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'

// About Us Profile Props
const aboutUsProfileProps = {
  id: types.maybeNull(types.number),
  number: types.maybeNull(types.number),
  content: types.maybeNull(types.string),
  imageURL: types.maybeNull(types.string),
  originalFileName: types.maybeNull(types.string),
  status: types.maybeNull(types.boolean),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
}

const AboutUsProfile = types.model('aboutUsProfile').props(aboutUsProfileProps)

const AboutUsProfiles = types.array(
  types.model('AboutUsProfiles').props({
    ...aboutUsProfileProps,
    aboutUsProfiles: types.maybeNull(types.array(types.model('AboutUsProfiles').props(aboutUsProfileProps))),
  }),
)

const AboutUsProfileStoreModel = types
  .model('aboutUsProfile')
  .props({
    aboutUsProfile_isLoading: types.optional(types.boolean, false),
    aboutUsProfile_isSaveLoading: types.optional(types.boolean, false),
    aboutUsProfile_aboutUsProfiles: types.optional(AboutUsProfiles, []),
    aboutUsProfile_aboutUsProfile: types.optional(AboutUsProfile, {}),
  })
  .actions(self => ({
    aboutUsProfile_SET_LOADING: (payload: boolean): void => {
      self.aboutUsProfile_isLoading = payload
    },
    aboutUsProfile_SET_SAVE_LOADING: (payload: boolean): void => {
      self.aboutUsProfile_isSaveLoading = payload
    },
    aboutUsProfile_SET_ABOUT_US_PROFILES: (payload: IAboutUsProfile[]): void => {
      self.aboutUsProfile_aboutUsProfiles = cast(payload?.map(list => ({ ...list, number: 1 })) || [])
    },
    aboutUsProfile_SET_ABOUT_US_PROFILE: (payload: IAboutUsProfile): void => {
      self.aboutUsProfile_aboutUsProfile = cast(payload)
    },
    aboutUsProfile_CLEAR_ABOUT_US_PROFILES: (): void => {
      self.aboutUsProfile_aboutUsProfiles = cast([])
    },
    aboutUsProfile_CLEAR_ABOUT_US_PROFILE: (): void => {
      self.aboutUsProfile_aboutUsProfile = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch about us profile
     *
     * @param {IAboutUsProfileGetAttrs} payload
     *
     * @return {any} any
     */
    aboutUsProfile_fetchAboutUsProfiles: flow(function* (payload: IAboutUsProfileGetAllAttrs) {
      self.aboutUsProfile_SET_LOADING(true)

      const response: IAboutUsProfileApiGetAllResponse =
        yield self.environment.aboutUsProfileApi.aboutUsProfile_getAboutUsProfiles({
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })
      if (response.ok) {
        self.aboutUsProfile_SET_ABOUT_US_PROFILES(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response error
        if (payload?.onError) payload.onError()
      }

      self.aboutUsProfile_SET_LOADING(false)
    }),
    /**
     * @description Fetch about us profile
     *
     * @param {IAboutUsProfileGetAttrs} payload
     *
     * @return {any} any
     */
    aboutUsProfile_fetchAboutUsProfile: flow(function* (payload: IAboutUsProfileGetAttrs) {
      self.aboutUsProfile_SET_LOADING(true)

      const response: IAboutUsProfileApiGetResponse = yield self.environment.aboutUsProfileApi.aboutUsProfile_getAboutUsProfile({
        params: { ...payload.params },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        self.aboutUsProfile_SET_ABOUT_US_PROFILE(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response error
        if (payload?.onError) payload.onError()
      }

      self.aboutUsProfile_SET_LOADING(false)
    }),

    /**
     * @description Save about us profile
     *
     * @param {IAboutUsProfileSaveAttrs} payload
     *
     * @return {any} any
     */
    aboutUsProfile_saveAboutUsProfile: flow(function* (payload: IAboutUsProfileSaveAttrs) {
      self.aboutUsProfile_SET_SAVE_LOADING(true)

      const response: IAboutUsProfileApiSaveResponse = yield self.environment.aboutUsProfileApi.aboutUsProfile_saveAboutUsProfile(
        {
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        },
      )
      if (response.ok) {
        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      self.aboutUsProfile_SET_SAVE_LOADING(false)
    }),
  }))

export const createAboutUsProfileStoreDefaultModel = () => types.optional(AboutUsProfileStoreModel, {})
